import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Aircom Auth Context
 * ───────────────────────────────────────────────────────────────────────────
 * SECURITY NOTE — localStorage usage:
 *   We persist the JWT access token in localStorage so the SPA can survive
 *   page reloads. This is convenient but exposes the token to any XSS payload
 *   that runs in this origin.
 *
 *   Mitigations in place:
 *     • Tokens are short-lived (24h, see backend JWT_EXPIRATION_HOURS).
 *     • CSP + React's escaping cover the most common XSS vectors.
 *     • No PII or financial values are written to localStorage from here
 *       (only the opaque token; user object lives in React state only).
 *     • resilientCache.js explicitly strips sensitive fields before caching.
 *
 *   Future hardening (P1 / backlog):
 *     • Move to httpOnly refresh cookie + in-memory access token.
 *     • Add CSP `script-src 'self'` strict headers from the proxy.
 *
 * Do NOT add additional sensitive data (profile, balance, secrets) to
 * localStorage from this file without a fresh threat-model review.
 */
const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  // Preview tokens (admin's "Vista previa como gestor") live in sessionStorage so
  // they are isolated per browser tab: the admin keeps their localStorage session
  // in the original tab while the preview tab uses a short-lived gestor JWT.
  const previewToken = typeof window !== 'undefined' ? window.sessionStorage.getItem('preview_token') : null;
  const initialToken = previewToken || localStorage.getItem('token');
  // Hydrate role synchronously from cache so the correct layout mounts on the
  // very first render. Without this, on slow networks/refresh the user briefly
  // sees a redirect to a login picker before /providers/me finishes — the
  // classic "me manda al acceso de proveedor al refrescar" bug.
  const cachedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const [user, setUser] = useState(initialToken && cachedRole ? { role: cachedRole, _hydrating: true } : null);
  const [token, setToken] = useState(initialToken);
  const [isPreviewSession] = useState(!!previewToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Prefer a preview token if the tab was launched in preview mode.
      const savedToken = window.sessionStorage.getItem('preview_token') || localStorage.getItem('token');
      if (!savedToken) {
        setLoading(false);
        return;
      }

      // Try the 3 identity probes in order. If a network error occurs (offline,
      // 5xx, abort) on a probe, do NOT blow away the token — keep the cached
      // user so the layout stays mounted. Only drop the session when we get
      // an unambiguous 401/403 from EVERY probe.
      const probes = [
        { url: `${API_URL}/auth/me`, map: (d) => d },
        { url: `${API_URL}/providers/me`, map: (d) => d },
        { url: `${API_URL}/client/profile`, map: (d) => ({ ...d, role: 'cliente' }) },
      ];
      let resolved = null;
      let sawNetworkError = false;
      for (const probe of probes) {
        try {
          const r = await axios.get(probe.url, { headers: { Authorization: `Bearer ${savedToken}` } });
          resolved = probe.map(r.data);
          break;
        } catch (err) {
          const status = err?.response?.status;
          // Only 401/403 means "this token is wrong for THIS probe" → try next.
          // Anything else (network, 5xx, timeout) is "we couldn't verify" →
          // keep the cached user, do not log out.
          if (status !== 401 && status !== 403) {
            sawNetworkError = true;
            break;
          }
        }
      }

      if (resolved) {
        setUser(resolved);
        setToken(savedToken);
        try { localStorage.setItem('user_role', resolved.role || ''); } catch { /* ignore quota */ }
      } else if (sawNetworkError && cachedRole) {
        // Keep the cached session — the next API call will redirect to login
        // organically if the token is truly invalid.
        setUser({ role: cachedRole, _hydrating: false, _stale: true });
        setToken(savedToken);
      } else {
        // All 3 probes returned 401/403 → token is dead.
        if (window.sessionStorage.getItem('preview_token')) {
          window.sessionStorage.removeItem('preview_token');
        } else {
          localStorage.removeItem('token');
        }
        try { localStorage.removeItem('user_role'); } catch { /* ignore */ }
        setToken(null);
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (userData?.role) localStorage.setItem('user_role', userData.role);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const clientLogin = async (email, password) => {
    const response = await axios.post(`${API_URL}/client/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user_role', userData?.role || 'cliente');
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const providerLogin = async (email, password) => {
    const response = await axios.post(`${API_URL}/providers/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    if (userData?.role) localStorage.setItem('user_role', userData.role);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const clientRegister = async (name, email, password, phone, referralCode) => {
    const response = await axios.post(`${API_URL}/client/register`, {
      name, email, password, phone, referral_code: referralCode || undefined
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user_role', userData?.role || 'cliente');
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    if (window.sessionStorage.getItem('preview_token')) {
      window.sessionStorage.removeItem('preview_token');
    }
    localStorage.removeItem('token');
    try { localStorage.removeItem('user_role'); } catch { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const isAdmin = () => user?.role === 'admin';
  const isGestor = () => user?.role === 'gestor';
  const isClient = () => user?.role === 'cliente' || user?.role === 'client';
  const isProvider = () => user?.role === 'payment_agent' || user?.role === 'ticket_provider';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!token,
      login,
      providerLogin,
      clientLogin,
      clientRegister,
      logout,
      getAuthHeaders,
      isAdmin,
      isGestor,
      isClient,
      isProvider,
      isPreviewSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
